import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useDeleteEntity } from "@/hooks/use-data";
import type { EntityRecord, TargetEnv } from "@/lib/types";
import { useMemo, useState } from "react";
import { TargetEnvRadio } from "./target-env-radio";

// IR-661 Rotem round-2 (2026-05-20): single-entity delete is a popup
// Dialog so it matches DeleteSelectedDialog (bulk) and GroupDeleteDialog
// (single group). Typed-confirm gesture mirrors single-group delete.

interface Props {
  entity: EntityRecord;
  onClose: () => void;
  onDeleted?: () => void;
}

function fmt(value: string | number | null | undefined): string {
  if (value == null || value === "") return "—";
  return String(value);
}

function envCopy(env: TargetEnv | null): string {
  if (env === null) return "Pick a target environment to enable Delete.";
  if (env === "both")
    return "Soft-deletes the entity from BOTH prod and dev. Memberships, group counts, and link cascades run per env.";
  const label = env === "prod" ? "production" : "development";
  return `Soft-deletes the entity from ${label} only. Memberships, group counts, and link cascades run on that env.`;
}

export function EntityDeleteDialog({ entity, onClose, onDeleted }: Props) {
  const [typed, setTyped] = useState("");
  const [targetEnv, setTargetEnv] = useState<TargetEnv | null>(null);
  const mutation = useDeleteEntity();
  const errorMessage = mutation.error?.message ?? null;

  const confirmEnabled = useMemo(() => {
    if (targetEnv === null) return false;
    if (typed === "DELETE") return true;
    if (entity.identifier && typed === entity.identifier) return true;
    return false;
  }, [typed, entity.identifier, targetEnv]);

  function handleConfirm() {
    if (targetEnv === null) return;
    mutation.mutate(
      { id: entity.id, targetEnv },
      {
        onSuccess: () => {
          onDeleted?.();
          onClose();
        },
      },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent style={{ display: "flex", flexDirection: "column", gap: 16, padding: 20 }}>
        <div>
          <DialogTitle>Delete entity</DialogTitle>
          <DialogDescription>{envCopy(targetEnv)}</DialogDescription>
        </div>

        <TargetEnvRadio value={targetEnv} onChange={setTargetEnv} actionVerb="delete" />

        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "max-content 1fr",
            rowGap: 6,
            columnGap: 12,
            fontSize: 12,
          }}
        >
          <dt style={{ color: "var(--iris-fg-4)" }}>Name</dt>
          <dd style={{ color: "var(--iris-fg-1)", fontWeight: 500 }}>{fmt(entity.name)}</dd>

          <dt style={{ color: "var(--iris-fg-4)" }}>Encrypted ID</dt>
          <dd style={{ color: "var(--iris-fg-2)", fontFamily: "var(--iris-font-mono)" }}>
            {fmt(entity.id)}
          </dd>

          <dt style={{ color: "var(--iris-fg-4)" }}>Identifier</dt>
          <dd style={{ color: "var(--iris-fg-2)", fontFamily: "var(--iris-font-mono)" }}>
            {fmt(entity.identifier)}
            {entity.identifier_type ? (
              <span
                style={{
                  marginLeft: 6,
                  borderRadius: "var(--iris-r-sm)",
                  background: "var(--iris-bg-4)",
                  padding: "2px 6px",
                  fontSize: 10,
                }}
              >
                {entity.identifier_type}
              </span>
            ) : null}
          </dd>

          <dt style={{ color: "var(--iris-fg-4)" }}>Platform</dt>
          <dd>{fmt(entity.platform)}</dd>

          <dt style={{ color: "var(--iris-fg-4)" }}>Status</dt>
          <dd>{fmt(entity.status)}</dd>

          <dt style={{ color: "var(--iris-fg-4)" }}>Risk</dt>
          <dd>{fmt(entity.risk_score)}</dd>

          <dt style={{ color: "var(--iris-fg-4)" }}>Groups</dt>
          <dd>{fmt(entity.group_count)}</dd>
        </dl>

        <div>
          <label
            htmlFor="ir661-entity-delete-confirm"
            style={{
              display: "block",
              fontSize: 12,
              color: "var(--iris-fg-3)",
              marginBottom: 6,
            }}
          >
            Type <strong>DELETE</strong>
            {entity.identifier ? (
              <>
                {" "}
                or <strong>{entity.identifier}</strong>
              </>
            ) : null}{" "}
            to confirm.
          </label>
          <input
            id="ir661-entity-delete-confirm"
            className="iris-input"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Type DELETE to confirm"
            autoComplete="off"
            style={{ width: "100%" }}
          />
        </div>

        {errorMessage ? (
          <div
            style={{
              fontSize: 12,
              color: "var(--iris-neon-pink)",
              padding: "8px 10px",
              border: "1px solid var(--iris-neon-pink)",
              borderRadius: 6,
            }}
          >
            {errorMessage}
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: "auto",
          }}
        >
          <button
            type="button"
            className="iris-btn"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="iris-btn iris-btn--primary"
            onClick={handleConfirm}
            disabled={!confirmEnabled || mutation.isPending}
          >
            {mutation.isPending ? "Deleting…" : "Delete entity"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
